import { z } from 'zod';

/**
 * Morning dispatch — which site each worker is sent to for one day.
 *
 * A worker with no dispatch row for a date is "in camp". The board is read and
 * written one company day at a time, keyed by the company-timezone calendar day
 * (docs/time-and-dates.md), never by the browser's clock.
 */

/** A real calendar day: `2026-02-31` has the right shape and is not one. */
const Ymd = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine((s) => {
    // zod runs a refine even after the regex failed, so an unparseable string
    // must answer false here rather than throw from toISOString().
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
  }, 'Not a calendar day');

export const DispatchDateParamSchema = z.object({ date: Ymd });
export type DispatchDateParam = z.infer<typeof DispatchDateParamSchema>;

/**
 * Which crew on a TWO-SHIFT site (`Worksite.twoShifts`): the morning lane or
 * the night lane. A one-shift site has no lane (null). One worker, one lane a
 * day — the row is still one per worker per day.
 */
export const DISPATCH_LANES = ['DAY', 'NIGHT'] as const;
export type DispatchLane = (typeof DISPATCH_LANES)[number];

/**
 * Send these workers to a site (`worksiteId`), or back to camp (`null`).
 * Capped so a single request cannot rewrite an unbounded roster; a company
 * dispatches its workforce in crews, not in one 10,000-row write.
 */
export const DispatchAssignSchema = z.object({
  employeeIds: z.array(z.string().min(1)).min(1).max(2000),
  worksiteId: z.string().min(1).nullable(),
  /** PASTE when the list came from "Paste codes"; MANUAL for a drag or a click. */
  source: z.enum(['MANUAL', 'PASTE']).default('MANUAL'),
  /**
   * The lane on a two-shift site — REQUIRED there, and must be absent or null
   * everywhere else (the server checks it against the site, which this schema
   * cannot see). Ignored for back-to-camp.
   */
  lane: z.enum(DISPATCH_LANES).nullable().optional(),
});
export type DispatchAssign = z.infer<typeof DispatchAssignSchema>;

/**
 * Copy an earlier day's plan onto this one. The board does it by itself when it
 * opens on a day with nobody placed (`onlyIfEmpty`): if anyone in the caller's
 * reach has a row on the day by the time the copy lands, nothing is written.
 */
export const DispatchCopySchema = z.object({ fromDate: Ymd, onlyIfEmpty: z.boolean().optional() });
export type DispatchCopy = z.infer<typeof DispatchCopySchema>;

/**
 * Move these workers to another camp from the day on (`campId`), and back into
 * the camp lane that day. `null` houses them in no camp — only an undo of a
 * move for someone who had none sends it; the board never offers it.
 */
export const DispatchCampMoveSchema = z.object({
  employeeIds: z.array(z.string().min(1)).min(1).max(2000),
  campId: z.string().min(1).nullable(),
});
export type DispatchCampMove = z.infer<typeof DispatchCampMoveSchema>;

/**
 * Why a worker cannot be sent to a site today. A code, not a sentence, so the
 * screen words it once (§3.6: one event, one reason).
 */
export const DISPATCH_BLOCK_REASONS = [
  'ON_LEAVE',
  'EMIRATES_ID_EXPIRED',
  'VISA_EXPIRED',
  'LABOUR_CARD_EXPIRED',
  'NOT_IN_SCOPE',
  /** Copying a day: the site they went to then has since been switched off. */
  'SITE_OFF',
  /** Filing open leave: an approved or pending leave already starts after the day. */
  'LEAVE_BOOKED',
  /** Filing leave with an end: not enough annual balance, and overdrafts are off. */
  'LEAVE_BALANCE',
] as const;
export type DispatchBlockReason = (typeof DISPATCH_BLOCK_REASONS)[number];
